import { get, head, isString, values } from "lodash";
import { DateTime } from "luxon";

export interface DebtorBlockActor {
  id: string;
  name: string | null;
  email: string;
}

export interface DebtorBlockEvent {
  id: string;
  action: "BLOCKED" | "UNBLOCKED";
  actor: DebtorBlockActor | null;
  reason: string;
  relations_changed: number;
  applies_to_future: boolean;
  created: string;
}

export interface DebtorBlockState {
  total_relations: number;
  blocked_relations: number;
  is_bulk_blocked: boolean;
  applies_to_future: boolean;
  latest_block_event: DebtorBlockEvent | null;
  event_count: number;
}

export const isBlank = (value: string): boolean => value.trim() === "";

export const actorName = (event: DebtorBlockEvent): string =>
  get(event, "actor.name") || get(event, "actor.email") || "Unknown";

export const formatEventTime = (iso: string): string =>
  DateTime.fromISO(iso).toFormat("LLL d, yyyy h:mm a");

export const showBanner = (state: DebtorBlockState): boolean =>
  state.is_bulk_blocked && state.blocked_relations === state.total_relations;

export const canUnblockAll = (state: DebtorBlockState): boolean =>
  state.blocked_relations > 0 || state.is_bulk_blocked;

export const barSummary = (state: DebtorBlockState): string =>
  state.blocked_relations === 0
    ? `0 of ${state.total_relations} debtors blocked. Block all stops factored funding requests for every debtor on this client.`
    : `${state.blocked_relations} of ${state.total_relations} debtors blocked individually.`;

export const bannerDetail = (state: DebtorBlockState): string => {
  const event = state.latest_block_event;
  const futureLine = state.applies_to_future
    ? "Debtors connected later: blocked"
    : "Debtors connected later: NOT blocked";
  if (!event) {
    return futureLine;
  }
  const reasonLine = event.reason ? ` · Reason: ${event.reason}` : "";
  return `${futureLine} · Blocked by ${actorName(event)}, ${formatEventTime(event.created)}${reasonLine}`;
};

export const errorMessage = (error: any, fallback: string): string => {
  const data = get(error, "response.data");
  if (isString(data)) {
    return data && !data.trim().startsWith("<") ? data : fallback;
  }
  const first = head(values(data || {}));
  const message = Array.isArray(first) ? head(first) : first;
  return isString(message) && message ? message : fallback;
};
